/**
 * band-readout.js — значение полосы там, где подпись под каждым ползунком не
 * помещается.
 *
 * ОТКУДА. Левон Айрапетов (Pro, 26.08.2026): «на 10-полосном видно, на сколько
 * ползунок дБ повышает или понижает, на 32-полосном кажется этого нет». Так и
 * было, и намеренно: в третьоктавной сетке колонка 20–28 px, подпись «−7.5»
 * шире, и её просто спрятали — `.dense .eq-band-db{display:none}` в /equalizer
 * и `.is-dense .mp-band-v{display:none}` в /audio-player. Две копии одного
 * решения «не влезает — убрать», и ни в одной не было замены.
 *
 * ЧТО ДЕЛАЕТ. Одна плавающая подпись на сетку — над ТОЙ полосой, с которой
 * человек работает: под курсором, под пальцем, в фокусе с клавиатуры, или
 * той, которую сейчас двигает график (любой `input` на ползунке). Текст даёт
 * тул (частота + дБ — в плотной сетке подписи частот тоже стоят не под всеми
 * колонками). Подпись живёт в строке, где у широкой сетки стоят числа, поэтому
 * колонка не меняет высоту, а у краёв сетки подпись прижимается внутрь, чтобы
 * её не срезал горизонтальный скролл.
 *
 * Замер 26.08 (стенд, 4 ширины экрана): статичные подписи под всеми 31
 * колонками перекрываются уже на 960 px, вертикальный текст помещается, но
 * поднимает колонку на ~30 px и читается хуже; у плавающей подписи ноль
 * перекрытий и ноль сдвига раскладки на всех ширинах.
 *
 * Подключение:
 *   window.__bandReadout.attach(gridEl, {
 *     band: '.eq-band',            // колонка полосы (position:relative ставим сами)
 *     slider: '.eq-band-slider',   // ползунок внутри колонки
 *     label: '.eq-band-db',        // строка, в которой живёт подпись (задаёт высоту)
 *     text: function(slider, band, i){ return '6.3k Hz +7.5 dB' },
 *     active: function(){ return grid.classList.contains('dense') }
 *   });
 * Перестройка сетки через innerHTML безопасна: слушатели делегированы,
 * подпись пересоздаётся, «горячая» полоса сбрасывается.
 */
(function () {
    'use strict';
    if (window.__bandReadout) return;

    var STYLE_ID = 'band-readout-style';
    function ensureStyle() {
        if (document.getElementById(STYLE_ID)) return;
        var st = document.createElement('style');
        st.id = STYLE_ID;
        st.textContent =
            '.band-readout{position:absolute;left:50%;top:0;transform:translate(-50%,-50%);' +
            'white-space:nowrap;direction:ltr;font-size:12px;font-weight:600;line-height:1;' +
            'font-variant-numeric:tabular-nums;padding:3px 6px;border-radius:5px;' +
            'background:var(--bg-secondary,#1e293b);color:var(--text-primary,#f1f5f9);' +
            'border:1px solid var(--border-hover,rgba(255,255,255,.18));box-shadow:0 2px 8px rgba(0,0,0,.25);' +
            'pointer-events:none;z-index:4}' +
            '.band-readout.is-positive{color:var(--accent,#38bdf8)}' +
            '.band-readout.is-negative{color:var(--warning,#fbbf24)}';
        document.head.appendChild(st);
    }

    function attach(grid, opts) {
        if (!grid || grid.__bandReadout) return grid && grid.__bandReadout;
        ensureStyle();
        var bandSel = opts.band, sliderSel = opts.slider, labelSel = opts.label;
        var textFn = opts.text, activeFn = opts.active || function () { return true; };
        var el = null, hotBand = null, dragging = false, pointerInside = false;

        function bands() { return Array.prototype.slice.call(grid.querySelectorAll(bandSel)); }
        function bandOf(target) {
            return target && target.closest ? target.closest(bandSel) : null;
        }
        function ensureEl() {
            if (el) return el;
            el = document.createElement('span');
            el.className = 'band-readout';
            el.setAttribute('aria-hidden', 'true');
            return el;
        }
        function clear() {
            if (hotBand) hotBand.classList.remove('is-hot');
            hotBand = null;
            if (el && el.parentNode) el.parentNode.removeChild(el);
        }
        function place(band) {
            var label = band.querySelector(labelSel);
            var r = ensureEl();
            if (r.parentNode !== band) band.appendChild(r);
            if (getComputedStyle(band).position === 'static') band.style.position = 'relative';
            // Середина строки подписи — та же высота, что у чисел широкой сетки.
            var top = label ? (label.offsetTop + label.offsetHeight / 2) : 0;
            r.style.top = top + 'px';
            r.style.marginLeft = '0px';
            // Не дать краевым полосам уехать за границу прокручиваемой сетки.
            var gr = grid.getBoundingClientRect(), rr = r.getBoundingClientRect();
            var pad = 2, shift = 0;
            if (rr.left < gr.left + pad) shift = gr.left + pad - rr.left;
            else if (rr.right > gr.right - pad) shift = gr.right - pad - rr.right;
            if (shift) r.style.marginLeft = shift + 'px';
        }
        function show(band) {
            if (!band || !activeFn()) { clear(); return; }
            var slider = band.querySelector(sliderSel);
            if (!slider) { clear(); return; }
            if (hotBand && hotBand !== band) hotBand.classList.remove('is-hot');
            hotBand = band;
            band.classList.add('is-hot');
            var i = bands().indexOf(band);
            var r = ensureEl();
            r.textContent = textFn(slider, band, i);
            var v = parseFloat(slider.value) || 0;
            r.classList.toggle('is-positive', v > 0);
            r.classList.toggle('is-negative', v < 0);
            place(band);
        }
        function refresh() { if (hotBand && hotBand.isConnected) show(hotBand); else clear(); }

        grid.addEventListener('pointerover', function (e) {
            pointerInside = true;
            if (dragging) return;
            var b = bandOf(e.target);
            if (b) show(b);
        });
        grid.addEventListener('pointerdown', function (e) {
            var b = bandOf(e.target);
            if (!b) return;
            dragging = true;
            show(b);
        });
        grid.addEventListener('pointerleave', function (e) {
            pointerInside = false;
            if (dragging) return;
            // Мышь ушла — подпись не нужна; палец «уходит» после каждого тапа,
            // а значение после тапа как раз и хотят увидеть — держим до тапа вне сетки.
            if (e.pointerType === 'touch' || e.pointerType === 'pen') return;
            var f = document.activeElement;
            if (f && grid.contains(f) && f.matches && f.matches(sliderSel)) { show(bandOf(f)); return; }
            clear();
        });
        function endDrag() {
            if (!dragging) return;
            dragging = false;
            if (!pointerInside && hotBand) {
                var f = document.activeElement;
                var keep = f && grid.contains(f) && f.matches && f.matches(sliderSel);
                if (!keep && !touchHeld) clear();
            }
        }
        var touchHeld = false;
        grid.addEventListener('pointerup', function (e) { if (e.pointerType !== 'mouse') touchHeld = true; endDrag(); });
        window.addEventListener('pointerup', function (e) { if (e.pointerType !== 'mouse') touchHeld = true; endDrag(); });
        window.addEventListener('pointercancel', endDrag);
        document.addEventListener('pointerdown', function (e) {
            if (!grid.contains(e.target)) { touchHeld = false; clear(); }
        }, true);
        grid.addEventListener('focusin', function (e) {
            if (e.target.matches && e.target.matches(sliderSel)) show(bandOf(e.target));
        });
        grid.addEventListener('focusout', function (e) {
            if (!(e.target.matches && e.target.matches(sliderSel))) return;
            if (pointerInside || dragging || touchHeld) return;
            clear();
        });
        // Любое изменение значения — с ползунка, с клавиатуры, с графика — показывает,
        // что именно поменялось. Слушатель на сетке регистрируется ПОСЛЕ тула,
        // поэтому текст берётся уже из обновлённого значения.
        grid.addEventListener('input', function (e) {
            if (!(e.target.matches && e.target.matches(sliderSel))) return;
            if (!activeFn()) { clear(); return; }
            show(bandOf(e.target));
        });
        // innerHTML-перестройка сетки: старая полоса исчезла — подпись тоже.
        if (window.MutationObserver) {
            new MutationObserver(function () { if (hotBand && !hotBand.isConnected) clear(); }).observe(grid, { childList: true });
        }

        var api = { show: show, clear: clear, refresh: refresh, hot: function () { return hotBand; }, element: function () { return el; } };
        grid.__bandReadout = api;
        return api;
    }

    window.__bandReadout = { attach: attach };
})();
